import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { usePageSeo } from "@/hooks/usePageSeo";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ToolContext } from "@/components/tools/ToolContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, BarChart3, Target, TrendingUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

type Language = 'es' | 'en';

interface KeywordAnalysis {
  keyword: string;
  count: number;
  density: number;
  recommendation: 'optimal' | 'low' | 'high';
}

interface TextAnalysis {
  totalWords: number;
  uniqueWords: number;
  keywords: KeywordAnalysis[];
  topWords: { word: string; count: number; density: number }[];
  readabilityScore: number;
}

const KeywordDensityPage = () => {
  const { language, url, esUrl, enUrl, onLanguageChange } = usePageSeo();
  const [text, setText] = useState('');
  const [targetKeyword, setTargetKeyword] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<TextAnalysis | null>(null);


  const content = {
    es: {
      title: "Analizador Densidad Palabras Clave - Luis ROC | SEO",
      description: "Analiza la densidad de palabras clave en tu contenido. Optimiza tu SEO con el análisis de keywords y recomendaciones de densidad.",
      pageTitle: "Analizador de Densidad de Palabras Clave",
      subtitle: "Optimiza la densidad de keywords en tu contenido SEO",
      textLabel: "Contenido a analizar",
      textPlaceholder: "Pega aquí el contenido que quieres analizar...",
      keywordLabel: "Palabra clave objetivo (opcional)",
      keywordPlaceholder: "Ej: marketing digital",
      analyzeButton: "Analizar Densidad",
      analyzing: "Analizando...",
      results: "Análisis de Densidad",
      textStats: "Estadísticas del Texto",
      totalWords: "Total de palabras",
      uniqueWords: "Palabras únicas",
      readabilityScore: "Puntuación de legibilidad",
      targetKeywordAnalysis: "Análisis Palabra Clave Objetivo",
      topKeywords: "Palabras Más Frecuentes",
      keyword: "Palabra Clave",
      count: "Apariciones",
      density: "Densidad",
      recommendation: "Recomendación",
      optimal: "Óptima",
      low: "Baja",
      high: "Alta",
      recommendations: "Recomendaciones SEO",
      rec1: "La densidad ideal está entre 1-3% para la palabra clave principal",
      rec2: "Evita el keyword stuffing (densidad >5%)",
      rec3: "Usa sinónimos y variaciones de tu palabra clave",
      rec4: "Mantén un contenido natural y de valor"
    },
    en: {
      title: "Keyword Density Analyzer - Luis ROC | SEO",
      description: "Analyze keyword density in your content. Optimize your SEO with keyword analysis and density recommendations.",
      pageTitle: "Keyword Density Analyzer",
      subtitle: "Optimize keyword density in your SEO content",
      textLabel: "Content to analyze",
      textPlaceholder: "Paste here the content you want to analyze...",
      keywordLabel: "Target keyword (optional)",
      keywordPlaceholder: "E.g: digital marketing",
      analyzeButton: "Analyze Density",
      analyzing: "Analyzing...",
      results: "Density Analysis",
      textStats: "Text Statistics",
      totalWords: "Total words",
      uniqueWords: "Unique words",
      readabilityScore: "Readability score",
      targetKeywordAnalysis: "Target Keyword Analysis",
      topKeywords: "Most Frequent Words",
      keyword: "Keyword",
      count: "Occurrences",
      density: "Density",
      recommendation: "Recommendation",
      optimal: "Optimal",
      low: "Low",
      high: "High",
      recommendations: "SEO Recommendations",
      rec1: "Ideal density is between 1-3% for main keyword",
      rec2: "Avoid keyword stuffing (density >5%)",
      rec3: "Use synonyms and variations of your keyword",
      rec4: "Keep content natural and valuable"
    }
  };

  const currentContent = content[language];

  const stopWords = language === 'es' 
    ? ['el', 'la', 'de', 'que', 'y', 'en', 'un', 'una', 'es', 'se', 'no', 'te', 'lo', 'le', 'da', 'su', 'por', 'son', 'con', 'para', 'al', 'del', 'los', 'las', 'pero', 'sus', 'fue', 'ser', 'todo', 'está', 'muy', 'ya', 'han', 'sin', 'más', 'sobre', 'me', 'hasta', 'hay', 'donde', 'mi', 'porque', 'qué', 'sólo', 'has', 'este', 'cuando', 'él', 'uno', 'poco', 'tiempo', 'puede', 'ser']
    : ['the', 'be', 'of', 'and', 'a', 'to', 'in', 'he', 'have', 'it', 'that', 'for', 'they', 'i', 'with', 'as', 'not', 'on', 'she', 'at', 'by', 'this', 'we', 'you', 'do', 'but', 'from', 'or', 'which', 'one', 'would', 'all', 'will', 'there', 'say', 'who', 'make', 'when', 'can', 'more', 'if', 'no', 'man', 'out', 'other', 'so', 'what', 'time', 'up', 'go', 'about'];

  const handleAnalyze = async () => {
    if (!text.trim()) return;
    
    setIsAnalyzing(true);
    
    setTimeout(() => {
      // Clean and process text
      const cleanText = text.toLowerCase().replace(/[^\w\s]/g, '');
      const words = cleanText.split(/\s+/).filter(word => word.length > 2);
      const totalWords = words.length;

      // Count word frequencies
      const wordCount: { [key: string]: number } = {};
      words.forEach(word => {
        if (!stopWords.includes(word)) {
          wordCount[word] = (wordCount[word] || 0) + 1;
        }
      });

      const uniqueWords = Object.keys(wordCount).length;

      // Get top words
      const topWords = Object.entries(wordCount)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 10)
        .map(([word, count]) => ({
          word,
          count,
          density: (count / totalWords) * 100
        }));

      // Analyze target keyword if provided
      const keywords: KeywordAnalysis[] = [];
      if (targetKeyword.trim()) {
        const targetCount = (text.toLowerCase().match(new RegExp(targetKeyword.toLowerCase(), 'g')) || []).length;
        const targetDensity = (targetCount / totalWords) * 100;
        
        let recommendation: 'optimal' | 'low' | 'high' = 'optimal';
        if (targetDensity < 1) recommendation = 'low';
        else if (targetDensity > 3) recommendation = 'high';

        keywords.push({
          keyword: targetKeyword,
          count: targetCount,
          density: targetDensity,
          recommendation
        });
      }

      // Calculate readability score (simplified)
      const avgWordsPerSentence = totalWords / (text.split(/[.!?]+/).length || 1);
      const readabilityScore = Math.max(0, Math.min(100, 100 - (avgWordsPerSentence * 2)));

      const textAnalysis: TextAnalysis = {
        totalWords,
        uniqueWords,
        keywords,
        topWords,
        readabilityScore
      };

      setAnalysis(textAnalysis);
      setIsAnalyzing(false);
    }, 1500);
  };

  const getRecommendationBadge = (recommendation: string) => {
    switch (recommendation) {
      case 'optimal':
        return <Badge variant="default">{currentContent.optimal}</Badge>;
      case 'low':
        return <Badge variant="secondary">{currentContent.low}</Badge>;
      case 'high':
        return <Badge variant="destructive">{currentContent.high}</Badge>;
      default:
        return null;
    }
  };

  const getDensityColor = (density: number) => {
    if (density >= 1 && density <= 3) return 'text-green-600';
    if (density < 1 || (density > 3 && density <= 5)) return 'text-yellow-600';
    return 'text-red-600';
  };

  const meta = {
    es: {
      title: "Analizador Densidad Keywords - Luis ROC | Optimización SEO",
      description: "Herramienta gratuita para analizar densidad de palabras clave. Optimiza tu contenido SEO con análisis de keywords profesional.",
      url: "https://luisroc.com/herramientas/densidad-palabras"
    },
    en: {
      title: "Keyword Density Analyzer - Luis ROC | SEO Optimization",
      description: "Free tool to analyze keyword density. Optimize your SEO content with professional keyword analysis.",
      url: "https://luisroc.com/tools/keyword-density"
    }
  };

  const currentMeta = meta[language];

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>{currentMeta.title}</title>
        <meta name="description" content={currentMeta.description} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={currentMeta.title} />
        <meta property="og:description" content={currentMeta.description} />
        <meta property="og:url" content={url} />
        <meta name="keywords" content="densidad palabras clave, keyword density, análisis SEO, optimización contenido" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={onLanguageChange} />
        
        <main className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold mb-4 flex items-center justify-center gap-2">
                <Target className="h-8 w-8 text-primary" />
                {currentContent.pageTitle}
              </h1>
              <p className="text-xl text-muted-foreground">
                {currentContent.subtitle}
              </p>
            </div>

            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="h-5 w-5" />
                  {currentContent.pageTitle}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {currentContent.keywordLabel}
                  </label>
                  <Input
                    placeholder={currentContent.keywordPlaceholder}
                    value={targetKeyword}
                    onChange={(e) => setTargetKeyword(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    {currentContent.textLabel}
                  </label>
                  <Textarea
                    placeholder={currentContent.textPlaceholder}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    rows={8}
                  />
                </div>

                <Button 
                  onClick={handleAnalyze} 
                  disabled={isAnalyzing || !text.trim()}
                  className="w-full"
                >
                  {isAnalyzing ? currentContent.analyzing : currentContent.analyzeButton}
                </Button>
              </CardContent>
            </Card>

            {analysis && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      {currentContent.results}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <h3 className="font-semibold mb-3">{currentContent.textStats}</h3>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="text-center p-4 bg-muted rounded-lg">
                          <p className="text-2xl font-bold text-primary">{analysis.totalWords}</p>
                          <p className="text-sm text-muted-foreground">{currentContent.totalWords}</p>
                        </div>
                        <div className="text-center p-4 bg-muted rounded-lg">
                          <p className="text-2xl font-bold text-primary">{analysis.uniqueWords}</p>
                          <p className="text-sm text-muted-foreground">{currentContent.uniqueWords}</p>
                        </div>
                        <div className="text-center p-4 bg-muted rounded-lg">
                          <p className="text-2xl font-bold text-primary">{analysis.readabilityScore.toFixed(0)}</p>
                          <p className="text-sm text-muted-foreground">{currentContent.readabilityScore}</p>
                        </div>
                      </div>
                    </div>

                    {analysis.keywords.length > 0 && (
                      <div>
                        <h3 className="font-semibold mb-3">{currentContent.targetKeywordAnalysis}</h3>
                        <div className="space-y-3">
                          {analysis.keywords.map((kw, index) => (
                            <div key={index} className="border rounded-lg p-4">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-medium">{kw.keyword}</h4>
                                {getRecommendationBadge(kw.recommendation)}
                              </div>
                              <div className="grid md:grid-cols-3 gap-4">
                                <div>
                                  <p className="text-sm text-muted-foreground">{currentContent.count}</p>
                                  <p className="font-medium">{kw.count}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-muted-foreground">{currentContent.density}</p>
                                  <p className={`font-medium ${getDensityColor(kw.density)}`}>
                                    {kw.density.toFixed(2)}%
                                  </p>
                                </div>
                                <div>
                                  <Progress value={Math.min(kw.density * 20, 100)} className="h-2" />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <h3 className="font-semibold mb-3">{currentContent.topKeywords}</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left p-2">{currentContent.keyword}</th>
                              <th className="text-left p-2">{currentContent.count}</th>
                              <th className="text-left p-2">{currentContent.density}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analysis.topWords.map((word, index) => (
                              <tr key={index} className="border-b">
                                <td className="p-2 font-medium">{word.word}</td>
                                <td className="p-2">{word.count}</td>
                                <td className="p-2">
                                  <span className={getDensityColor(word.density)}>
                                    {word.density.toFixed(2)}%
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-blue-50 border-blue-200">
                  <CardHeader>
                    <CardTitle className="text-blue-800 flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      {currentContent.recommendations}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-blue-700">
                    <ul className="space-y-2">
                      <li>• {currentContent.rec1}</li>
                      <li>• {currentContent.rec2}</li>
                      <li>• {currentContent.rec3}</li>
                      <li>• {currentContent.rec4}</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
          <div className="max-w-4xl mx-auto">
            <ToolContext tool="keyword-density" language={language} />
          </div>
        </main>
        
        <Footer language={language} />
      </div>
    </>
  );
};

export default KeywordDensityPage;